import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { groupSessionService, expertService } from '../api/services';
import useApi from '../hooks/useApi';
import { useAuth } from '../context/AuthContext';
import DashboardLayout from '../components/layout/DashboardLayout';
import {
  Video, Mic, MessageCircle, Users, Plus, LogIn,
  Calendar, Clock, Copy, CheckCircle2, XCircle,
  AlertCircle, Loader2, ChevronRight, Share2, Star,
} from 'lucide-react';
import toast from 'react-hot-toast';

const SESSION_ICON  = { VIDEO: Video, AUDIO: Mic, CHAT: MessageCircle };
const STATUS_STYLE  = {
  SCHEDULED: 'bg-amber-50 text-amber-700 border-amber-200',
  LIVE:      'bg-green-50 text-green-700 border-green-200',
  COMPLETED: 'bg-forest-50 text-forest-700 border-forest-200',
  CANCELLED: 'bg-cream-100 text-forest-400 border-cream-200',
};

// ── Join Code Card ─────────────────────────────────────────────
const JoinByCodeCard = () => {
  const navigate = useNavigate();
  const [code,    setCode]    = useState('');
  const [joining, setJoining] = useState(false);

  const handleJoin = async (e) => {
    e.preventDefault();
    if (!code.trim()) { toast.error('Enter a join code'); return; }
    setJoining(true);
    try {
      const res     = await groupSessionService.joinByCode(code.trim().toUpperCase());
      const session = res.data?.data?.session ?? res.data?.session;
      toast.success('Joined! Taking you to the session...');
      navigate(`/group-sessions/${session.id}`);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Invalid join code');
    } finally { setJoining(false); }
  };

  return (
    <div className="bg-forest-900 rounded-2xl p-6">
      <div className="flex items-center gap-3 mb-4">
        <div className="w-10 h-10 bg-forest-700 rounded-xl flex items-center justify-center">
          <LogIn size={18} className="text-forest-300" />
        </div>
        <div>
          <h3 className="font-serif text-lg font-semibold text-cream-50">Join a Group Session</h3>
          <p className="text-xs text-forest-400">Enter the code shared with you (not needed if you were already booked as expert)</p>
        </div>
      </div>
      <form onSubmit={handleJoin} className="flex gap-3">
        <input
          value={code}
          onChange={e => setCode(e.target.value.toUpperCase())}
          placeholder="e.g. TOUR-4829"
          maxLength={12}
          className="flex-1 px-4 py-2.5 rounded-xl border border-forest-600 bg-forest-800 text-cream-50 placeholder-forest-500 text-sm font-mono tracking-wider focus:outline-none focus:border-forest-400"
        />
        <button type="submit" disabled={joining}
          className="px-5 py-2.5 bg-forest-600 hover:bg-forest-500 text-cream-50 text-sm font-semibold rounded-xl transition-colors disabled:opacity-60 flex items-center gap-2">
          {joining ? <Loader2 size={14} className="animate-spin" /> : 'Join →'}
        </button>
      </form>
    </div>
  );
};

// ── Session Card ───────────────────────────────────────────────
const SessionCard = ({ session, currentUserId, onCancelled }) => {
  const Icon        = SESSION_ICON[session.sessionType] ?? Video;
  const isHost      = session.hostUserId === currentUserId;
  const myRole      = session.participants?.find(p => p.userId === currentUserId)?.role ?? 'PARTICIPANT';
  const isLive      = session.status === 'LIVE';
  const isScheduled = session.status === 'SCHEDULED';
  const [copied,    setCopied]    = useState(false);
  const [cancelling, setCancelling] = useState(false);

  const date = session.scheduledAt
    ? new Date(session.scheduledAt).toLocaleString('en-GH', { dateStyle: 'long', timeStyle: 'short' })
    : '—';

  const handleCopy = () => {
    navigator.clipboard.writeText(session.joinCode);
    setCopied(true);
    toast.success('Join code copied!');
    setTimeout(() => setCopied(false), 2000);
  };

  const handleShare = () => {
    const text = `Join my TourEnt Hub group session!\nCode: ${session.joinCode}\nDate: ${date}`;
    if (navigator.share) {
      navigator.share({ title: session.title, text });
    } else {
      navigator.clipboard.writeText(text);
      toast.success('Session details copied — share with your group!');
    }
  };

  const handleCancel = async () => {
    if (!window.confirm('Cancel this group session?')) return;
    setCancelling(true);
    try {
      await groupSessionService.cancel(session.id);
      toast.success('Session cancelled');
      onCancelled?.();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Could not cancel');
    } finally { setCancelling(false); }
  };

  return (
    <div className={`bg-white border-2 rounded-2xl overflow-hidden transition-all hover:shadow-sm ${
      isLive ? 'border-green-300' : 'border-cream-200'
    }`}>
      {isLive && (
        <div className="bg-green-500 px-4 py-1.5 flex items-center gap-2">
          <span className="w-2 h-2 rounded-full bg-white animate-pulse" />
          <span className="text-xs font-semibold text-white uppercase tracking-widest">Live Now</span>
        </div>
      )}
      <div className="p-5">
        <div className="flex items-start gap-4">
          <div className="w-11 h-11 bg-forest-100 rounded-xl flex items-center justify-center flex-shrink-0">
            <Icon size={18} className="text-forest-700" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <div>
                <div className="flex items-center gap-2 flex-wrap">
                  <h4 className="font-serif font-semibold text-forest-900 text-base">{session.title}</h4>
                  {/* Show role badge */}
                  {myRole === 'EXPERT' && (
                    <span className="text-[10px] font-semibold bg-amber-50 text-amber-700 border border-amber-200 px-2 py-0.5 rounded-full flex items-center gap-1">
                      <Star size={9} /> You're the Expert
                    </span>
                  )}
                  {myRole === 'HOST' && (
                    <span className="text-[10px] font-semibold bg-forest-50 text-forest-700 border border-forest-200 px-2 py-0.5 rounded-full">Host</span>
                  )}
                </div>
                <div className="flex items-center gap-3 mt-1 text-xs text-forest-500 flex-wrap">
                  <span className="flex items-center gap-1"><Calendar size={10} /> {date}</span>
                  <span className="flex items-center gap-1"><Clock size={10} /> {session.duration} mins</span>
                  <span className="flex items-center gap-1">
                    <Users size={10} /> {session.participants?.length ?? 0}/{session.maxParticipants}
                  </span>
                </div>
                {session.expertProfile && myRole !== 'EXPERT' && (
                  <p className="text-xs text-forest-400 mt-1">
                    Expert: {session.expertProfile.user?.firstName} {session.expertProfile.user?.lastName}
                  </p>
                )}
              </div>
              <span className={`text-[10px] font-semibold border px-2.5 py-1 rounded-full flex-shrink-0 ${STATUS_STYLE[session.status]}`}>
                {session.status}
              </span>
            </div>

            {/* Join code — show to host for sharing */}
            {isHost && (isScheduled || isLive) && (
              <div className="mt-3 flex items-center gap-2 bg-cream-50 border border-cream-200 rounded-xl px-4 py-2.5">
                <div className="flex-1">
                  <p className="text-[10px] text-forest-400 uppercase tracking-widest mb-0.5">Join Code</p>
                  <p className="font-mono font-bold text-forest-900 text-base tracking-widest">{session.joinCode}</p>
                </div>
                <button onClick={handleCopy} title="Copy code"
                  className="p-2 text-forest-400 hover:text-forest-700 rounded-lg hover:bg-white transition-colors">
                  {copied ? <CheckCircle2 size={16} className="text-green-500" /> : <Copy size={16} />}
                </button>
                <button onClick={handleShare} title="Share session"
                  className="p-2 text-forest-400 hover:text-forest-700 rounded-lg hover:bg-white transition-colors">
                  <Share2 size={16} />
                </button>
              </div>
            )}

            {/* Participants avatars */}
            {session.participants?.length > 0 && (
              <div className="flex items-center gap-1 mt-3">
                {session.participants.slice(0, 6).map((p, i) => (
                  <div key={i} className="w-7 h-7 bg-forest-600 rounded-full flex items-center justify-center text-cream-50 text-[10px] font-bold border-2 border-white -ml-1 first:ml-0 overflow-hidden">
                    {p.user?.avatar
                      ? <img src={p.user.avatar} alt="" className="w-full h-full object-cover" />
                      : `${p.user?.firstName?.[0] ?? '?'}`}
                  </div>
                ))}
                {session.participants.length > 6 && (
                  <div className="w-7 h-7 bg-cream-200 rounded-full flex items-center justify-center text-forest-600 text-[10px] font-bold border-2 border-white -ml-1">
                    +{session.participants.length - 6}
                  </div>
                )}
                <span className="text-xs text-forest-400 ml-2">{session.participants.length} joined</span>
              </div>
            )}
          </div>
        </div>

        {/* Actions */}
        <div className="flex gap-2 mt-4 pt-4 border-t border-cream-100 flex-wrap">
          {(isLive || isScheduled) && (
            <Link to={`/group-sessions/${session.id}/room`}
              className="btn-primary text-xs py-1.5 px-4 flex items-center gap-1.5">
              <Video size={12} /> {isLive ? 'Join Now' : 'Enter Room'} →
            </Link>
          )}
          <Link to={`/group-sessions/${session.id}`}
            className="text-xs font-medium text-forest-600 border border-cream-200 hover:border-forest-400 px-3 py-1.5 rounded-full transition-colors">
            View Details →
          </Link>
          {isHost && isScheduled && (
            <button onClick={handleCancel} disabled={cancelling}
              className="text-xs px-3 py-1.5 rounded-full border border-rust-200 text-rust-500 hover:bg-rust-50 transition-colors font-medium ml-auto disabled:opacity-60">
              {cancelling ? <Loader2 size={11} className="animate-spin" /> : 'Cancel'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

// ── Main Page ──────────────────────────────────────────────────
const GroupSessionsPage = () => {
  const { user }         = useAuth();
  const [tab, setTab]    = useState('ALL');
  const [refetch, setRefetch] = useState(0);

  const TABS = ['ALL', 'SCHEDULED', 'LIVE', 'COMPLETED', 'CANCELLED'];

  const params = { page: 1, limit: 20 };
  if (tab !== 'ALL') params.status = tab;

  const { data, loading, error } = useApi(
    () => groupSessionService.getMySessions(params),
    [tab, refetch],
  );

  // ApiResponse.paginated puts array directly in res.data.data
  const sessions = Array.isArray(data?.data)          ? data.data
    : Array.isArray(data?.data?.sessions)              ? data.data.sessions
    : Array.isArray(data?.sessions)                    ? data.sessions
    : Array.isArray(data)                              ? data : [];

  return (
    <DashboardLayout title="Group Sessions">
      <div className="max-w-3xl space-y-6">

        {/* Top actions */}
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="flex gap-2 flex-wrap">
            {TABS.map(t => (
              <button key={t} onClick={() => setTab(t)}
                className={`text-xs font-medium px-3 py-1.5 rounded-full border transition-all ${
                  tab === t
                    ? 'bg-forest-800 text-cream-50 border-forest-800'
                    : 'bg-white text-forest-600 border-cream-200 hover:border-forest-400'
                }`}>
                {t}
              </button>
            ))}
          </div>
          <Link to="/group-sessions/create"
            className="btn-primary flex items-center gap-2 text-sm flex-shrink-0">
            <Plus size={14} /> New Group Session
          </Link>
        </div>

        {/* Join by code */}
        <JoinByCodeCard />

        {/* Error */}
        {error && (
          <div className="flex items-center gap-3 text-rust-500 bg-rust-50 border border-rust-200 rounded-xl px-4 py-3">
            <AlertCircle size={16} /> Could not load sessions.
          </div>
        )}

        {/* Sessions list */}
        {/* Expert auto-join notice */}
        {sessions.some(s => s.participants?.find(p => p.userId === user?.id)?.role === 'EXPERT') && (
          <div className="flex items-start gap-3 bg-amber-50 border border-amber-200 rounded-2xl px-4 py-3">
            <Star size={15} className="text-amber-500 flex-shrink-0 mt-0.5" />
            <p className="text-xs text-amber-700 leading-relaxed">
              <strong>You've been booked as an expert</strong> on some sessions below. You don't need a join code — just click <strong>Enter Room</strong> to join when the session starts.
            </p>
          </div>
        )}

        {loading ? (
          Array.from({ length: 3 }).map((_, i) => (
            <div key={i} className="h-40 bg-white border border-cream-200 rounded-2xl animate-pulse" />
          ))
        ) : sessions.length === 0 ? (
          <div className="text-center py-16 bg-white border border-cream-200 rounded-2xl text-forest-400">
            <Users size={40} className="mx-auto mb-4 opacity-30" />
            <p className="font-serif text-xl text-forest-600">No group sessions yet</p>
            <p className="text-sm mt-2 mb-6">Create one or join with a code shared by someone.</p>
            <Link to="/group-sessions/create" className="btn-primary inline-flex items-center gap-2">
              <Plus size={14} /> Create Group Session
            </Link>
          </div>
        ) : (
          <div className="space-y-4">
            {sessions.map(s => (
              <SessionCard
                key={s.id}
                session={s}
                currentUserId={user?.id}
                onCancelled={() => setRefetch(r => r + 1)}
              />
            ))}
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default GroupSessionsPage;